import { Box, Button, Card, SelectInput, TextareaInput, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { ActionOnAllocationAccountsResponse, IncentiveAssignmentStatus } from 'types';

import { useGetIncentiveQuery, useUnallocateAccountsFromAllocationMutation } from '../../services/incentives';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { ActionAccountsResult } from './ActionAccountsResult';

const validationSchema = yup.object({
  existingAllocationId: yup.string().required('Please select an allocation'),
  accountIds: yup.string().required('Please enter at least one account ID'),
});

export const UnallocateAccounts = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { data: incentive, isFetching } = useGetIncentiveQuery({ key: key! });
  const [unallocateAccounts] = useUnallocateAccountsFromAllocationMutation();
  const [result, setResult] = useState<ActionOnAllocationAccountsResponse | null>(null);

  const existingAllocations = incentive?.allocations || [];
  const allocationOptions = existingAllocations.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  const formik = useFormik({
    initialValues: {
      existingAllocationId: '',
      accountIds: '',
      reason: '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values: FormikValues, { setSubmitting }) => {
      try {
        const accountIdList = values.accountIds
          .split(/[\n,]+/)
          .map((id: string) => id.trim())
          .filter((id: string) => id.length > 0);

        if (accountIdList.length === 0) {
          toast.error('Please enter at least one valid account ID');
          setSubmitting(false);
          return;
        }

        let unallocateResult: ActionOnAllocationAccountsResponse;

        unallocateResult = await unallocateAccounts({
          key: key!,
          allocationId: values.existingAllocationId,
          accountIds: accountIdList,
        }).unwrap();

        if (unallocateResult.actionedAccounts.length > 0) {
          toast.success(`Successfully unallocated ${unallocateResult.actionedAccounts.length} account(s)`);
        }

        if (unallocateResult.invalidAccounts.length > 0 || unallocateResult.accountsAlreadyActioned.length > 0) {
          toast.error('Some accounts could not be unallocated. See details below.');
        }
        setResult(unallocateResult);
      } catch (error: any) {
        console.error('Error un-allocating accounts:', error);
        toast.error(error?.data?.message || 'Error un-allocating accounts');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleBack = () => {
    navigate(`/admin/incentives/${key}`);
  };

  if (result) {
    return (
      <ActionAccountsResult
        result={result}
        action={IncentiveAssignmentStatus.unallocated}
        onAllocateMore={() => {
          setResult(null);
          formik.resetForm();
        }}
        onBack={handleBack}
        incentiveKey={key!}
      />
    );
  }
  return (
    <>
      <Helmet>
        <title>Unallocate Accounts - {incentive?.name ?? 'Incentive'}</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          eyebrow={<Link to={`/admin/incentives/${key}`}>{incentive?.name || 'Incentive'}</Link>}
          title="Un-Allocate Accounts"
          description="Un-Allocate accounts for this incentive"
        />

        <Box padding="lg 0">
          <form onSubmit={formik.handleSubmit}>
            <Box direction="column" childGap="lg" maxWidth="800px">
              <Card padding="lg" overflow="visible">
                <Box as="h3" fontWeight="medium" fontSize="md" margin="0 0 md 0">
                  Allocation
                </Box>
                <SelectInput
                  label="Select Allocation"
                  name="existingAllocationId"
                  id="existingAllocationId"
                  value={allocationOptions.find((opt) => opt.value === formik.values.existingAllocationId) || null}
                  onChange={(event) => formik.setFieldValue('existingAllocationId', event.target.value?.value || '')}
                  onBlur={formik.handleBlur}
                  error={formik.touched.existingAllocationId && (formik.errors.existingAllocationId as string)}
                  options={allocationOptions}
                  isRequired
                  isDisabled={isFetching}
                />
              </Card>
              <Card padding="lg">
                <Box as="h3" fontWeight="medium" fontSize="md" margin="0 0 md 0">
                  Accounts
                </Box>
                <TextareaInput
                  label="Account IDs"
                  name="accountIds"
                  id="accountIds"
                  value={formik.values.accountIds}
                  onChange={(event) => formik.setFieldValue('accountIds', event.target.value)}
                  onBlur={formik.handleBlur}
                  error={formik.touched.accountIds && (formik.errors.accountIds as string)}
                  rows={6}
                  helpText="Enter account IDs separated by commas or new lines"
                  isRequired
                />
              </Card>

              <Box direction="row" childGap="md" justifyContent="flex-end">
                <Button variant="secondary" tone="neutral" size="md" onClick={handleBack}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
                  Un-Allocate Accounts
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </MainContainer>
    </>
  );
};
